import { defineStore } from "pinia";

import api from "@/api";
import { useUserStore } from "./user";
import { useTeamStore } from "./team";
import { statusToOptionsMapping, InteractionStatus2Enum, TicketResponseStatusEnum } from "@/utils/constants";
import { TAB_VALUE } from "@/constants/interaction";
import { TrainingStatusEnum } from "@/constants/trainings.js";
import { isStructEmpty } from "@/utils/utils";
import { useDepartmentStore } from "./department";
import { InteractionReplyMode2Enum, BASE_TIME_FORMAT, BASE_TIMEZONE } from "@/utils/constants";
import { formatInTimeZone } from "@/utils/dates";
import { HistoryTargetClass2Enum } from "@/constants/history";
import { useToast } from "vue-toastification";

const toast = useToast();

export const useInteractionDetailStore = defineStore("interactionDetail", {
    state: () => ({
        interaction_id: null,
        loaded: false,

        interaction: {},
        allTagOptions: [],
        relatedInteractions: [],
        training: {},

        ticketResponses: [],
        ticketResponseDrafts: null,
        comments: null,
        historiesByDay: null,

        loading: false,
        isConvertToTicketModalOpen: false,
        isReopenTicketModalOpen: false,
        isCloseTicketModalOpen: false,
        isTrainingPopupOpen: false,
        isEditorOpen: false,
        isReadOnlyMode: false,

        activeTab: null,
        draftResponse: {},
        editorValues: {},
        replyMode: InteractionReplyMode2Enum.REPLY_ALL,

        // for frontend temporary usage
        tempComment: "",
    }),
    getters: {
        tagSet: (state) => {
            return new Set(state.interaction.user_tags);
        },
        seenBySet: (state) => {
            return new Set(state.interaction.seen_by);
        },
        isSeen: (state) => {
            const userStore = useUserStore();
            const currentUserId = userStore.authUserId;
            return state.seenBySet.has(currentUserId);
        },
        showAsUnread: (state) => {
            const userStore = useUserStore();
            const teamStore = useTeamStore();
            const currentUserId = userStore.authUserId;
            const validInteractionStatuses = new Set([
                InteractionStatus2Enum.TICKET,
                InteractionStatus2Enum.SOFT_ALERT,
                InteractionStatus2Enum.TICKET_ON_HOLD,
                InteractionStatus2Enum.TICKET_CLOSED,
            ]);

            const assignedTeamId = state.interaction.teams?.length > 0 ? state.interaction.teams[0] : [];
            const teamUsers = teamStore.allDict[assignedTeamId]?.users || [];
            const assignedUsers = state.interaction.assignees || [];
            const isAssignedToCurrentUser = assignedUsers.includes(currentUserId) || teamUsers.includes(currentUserId);


            return validInteractionStatuses.has(state.interaction.status) && !state.seenBySet.has(currentUserId) && isAssignedToCurrentUser;
        },
        tagOptions: (state) => {
            return state.allTagOptions.filter((tag) => !state.tagSet.has(tag))
        },
        statusDropdownOptions: (state) => {
            return statusToOptionsMapping[state.interaction.status];
        },
        relatedInteractionIds: (state) => {
            return new Set(state.relatedInteractions.map((interaction) => interaction._id));
        },
        reversedRelatedInteractions: (state) => {
            return state.relatedInteractions.slice().reverse();
        },
        isInteractionLoaded: (state) => {
            return !isStructEmpty(state.interaction);
        },
        isUserTrainingApprover: (state) => {
            const userStore = useUserStore();
            const departmentStore = useDepartmentStore();

            const departmentUserIds = departmentStore.allDict[state.training.department]?.training_approvers_acc_ids || []
            return departmentUserIds.includes(userStore.authUserId);
        },
        hasExistingTraining: (state) => {
            return state.training._id !== undefined;
        },
        isDraftResponseLoaded: (state) => {
            return !isStructEmpty(state.draftResponse);
        },
        isTicketResponsesLoaded: (state) => {
            return state.ticketResponses != null;
        },
        isTicketResponseDraftsLoaded: (state) => {
            return state.ticketResponseDrafts != null;
        },
        isCommentsLoaded: (state) => {
            return state.comments != null;
        },
        isHistoriesLoaded: (state) => {
            return state.historiesByDay != null;
        },
        isEditorLoaded: (state) => {
            return !isStructEmpty(state.editorValues);
        },
        isEmptyTicket: (state) => {
            console.log("ticketResponses", state.ticketResponses);
            if (
                (state.ticketResponses instanceof Array) &&
                state.ticketResponses.length < 1
            ) {
              return true;
            }

            return false;
        },
    },
    actions: {
        async _loadInteraction(interactionId) {
            try {
                this.interaction = await api.interactions.getInteraction(interactionId);
                console.log("this.interaction: ", this.interaction)
                return this.interaction;
            } catch (err) {
                toast.error("Failed to load interaction");
                console.error(err);
                // No way to gracefully fail without interaction
                throw err;
            }
        },
        async _loadTags(interaction) {
            try {
                const allTagsResp = await api.interaction_tags.fetchAll(interaction.company_id)
                if (allTagsResp.data.length > 0) {
                    this.allTagOptions = allTagsResp.data[0].tags;
                }

            } catch (err) {
                // toast.error("Failed to load tags");
                console.error(err);
            }
        },
        async _loadRelatedInteractions(interaction) {
            try {
                this.relatedInteractions = await api.interactions.getRelevantInteractions(interaction._id);
            } catch (err) {
                toast.error("Failed to load related interactions");
                console.error(err);
            }

            if (this.relatedInteractions.length == 0) {
                this.relatedInteractions = [interaction];
            }
        },
        async _loadTraining(interaction) {
            try {
                this.training = await api.trainings.getTrainingByInteractionId(interaction._id);
            } catch (err) {
                // toast.error("Failed to load training");
                console.error(err);
            }

            if (this.training === null) {
                // No training - use default
                this.training = _getDefaultTraining(interaction);
            }
        },
        async _fetchTicketResponses(interaction = this.interaction) {
            const userStore = useUserStore();
            // Static for now, but BE is setup to take dynamic properties
            const ticketResponseFilters = {
                user_id: userStore.authUserId,
                to_user_ids: [userStore.authUserId],
            }
            try {
                const ticketResponses = await api.ticket_responses.fetchTicketResponsesByInteractionId(
                    interaction._id,
                    ticketResponseFilters
                );
                console.log("ticketResponses:" , ticketResponses)
                this.ticketResponses = ticketResponses
                console.log("this.ticketResponses: ", this.ticketResponses)
            } catch (err) {
                toast.error("Failed to load ticket responses");
                console.error(err);

                this.ticketResponses = [];
            }

        },
        async _loadTicketResponses(interaction = this.interaction) {
            try {
                console.log("interaction: ", interaction)
                const interactionId = interaction._id ?? interaction.id;
                const ticketResponses = await api.ticket_responses.fetchAllTicketResponsesByInteractionIdAndStatus(
                    interactionId,
                    TicketResponseStatusEnum.LIVE,
                );
                console.log("ticketResponses: ", ticketResponses)
                this.ticketResponses = ticketResponses
            } catch (err) {
                toast.error("Failed to load ticket responses");
                console.error(err);

                this.ticketResponses = [];
            }
        },
        async _loadTicketResponsesByThreadId(threadId) {
          try {
            const ticketResponses = await api.ticket_responses.getTicketResponsesByThreadId({
                threadId: threadId,
            });
            console.log("ticketResponses: ", ticketResponses)
            this.ticketResponses = ticketResponses
          } catch (err) {
            toast.error("Failed to load ticket responses");
            console.error(err);

            this.ticketResponses = [];
          }
        },
        async _loadComments(interaction) {
            this.interaction = interaction;
            try {
                this.comments = await api.comments.fetchAllInteractionComments(
                    interaction.id,
                );
            } catch (err) {
                // toast.error("Failed to load comments");
                console.error(err);

                this.comments = [];
            }

        },
        async _loadDrafts(interaction = this.interaction) {
            const interactionId =
                interaction?._id ??
                interaction?.id ??
                this.draftResponse?.interaction_id ??
                this.draftResponse?.id;
            if (!interactionId) {
                this.ticketResponseDrafts = [];
                return;
            }
            try {
                this.ticketResponseDrafts = await api.ticket_responses.fetchAllTicketResponsesByInteractionIdAndStatus(
                    interactionId,
                    TicketResponseStatusEnum.DRAFT,
                );
            } catch (err) {
                // toast.error("Failed to load drafts");
                console.error(err);

                this.ticketResponseDrafts = [];
            }
        },
        async _loadHistories(thread_id) {
            let histories = [];
            try {
                histories = await api.history.fetchHistory(
                    thread_id,
                );
            } catch (err) {
                // toast.error("Failed to load histories");
                console.error(err);
            }

            this.historiesByDay = histories.slice().reduce((groups, event) => {
                const eventDate = formatInTimeZone(
                    event.timestamp,
                    BASE_TIMEZONE,
                    "DD MMMM YYYY",
                );
                if (!groups[eventDate]) {
                    groups[eventDate] = [];
                }
                groups[eventDate].push({
                    ...event,
                    timestamp: formatInTimeZone(
                        event.timestamp,
                        BASE_TIMEZONE,
                        BASE_TIME_FORMAT,
                    ),
                });
                return groups;
            }, {});
        },
        // NOTE: This is firing multiple times per route change
        async load(interactionId, tab, isTrainingOpen) {
            console.log("START LOAD with id:", interactionId)

            if (this.loaded && this.interaction_id === interactionId) {
                return this.interaction;
              }
            this.$reset();          
            console.log("AFTER RESET this.interaction:", this.interaction)
          
            this.interaction_id = interactionId;
            this.loading = true;
          
            console.log("CALLING _loadInteraction...")
            const interaction = await this._loadInteraction(interactionId);
            console.log("API RESULT:", interaction)
            await Promise.all([this._loadRelatedInteractions(interaction), this._loadTags(interaction), this._loadTraining(interaction), this._loadDrafts(interaction)])
                // this._loadTicketResponses(interaction)

            this.activeTab = Object.values(TAB_VALUE).includes(tab)
                ? tab
                : this.interaction.has_ticket ? TAB_VALUE.CONVERSATIONS : TAB_VALUE.COMMENTS;
            // this.isEditorOpen = [InteractionStatus2Enum.TICKET, InteractionStatus2Enum.TICKET_ON_HOLD].includes(interaction.status);
            this.isEditorOpen = false
            this.isTrainingPopupOpen = isTrainingOpen ? isTrainingOpen == "true" : false;

            this.loaded = true;
            this.loading = false;
            return interaction
        },
        async loadReadOnly(interactionId, tab) {
            if (this.interaction_id == interactionId && (this.loading || this.loaded)) {
                return
            }

            this.$reset();
            this.interaction_id = interactionId
            this.loading = true;

            const interaction = await this._loadInteraction(interactionId);

            // these calls dont depend on eachother, run together
            // await Promise.allSettled([ this._loadRelatedInteractions(interaction), this._fetchTicketResponses(interaction, ticketResponseFilters)])
            await this._loadRelatedInteractions(interaction)
            this._fetchTicketResponses(interaction)

            this.activeTab = Object.values(TAB_VALUE).includes(tab)
                ? tab
                : this.interaction.has_ticket ? TAB_VALUE.CONVERSATIONS : TAB_VALUE.COMMENTS;
            this.isEditorOpen = [InteractionStatus2Enum.TICKET, InteractionStatus2Enum.TICKET_ON_HOLD].includes(interaction.status);
            this.isTrainingPopupOpen = false;

            this.loaded = true;
            this.loading = false;
        },
  
        async markInteractionAsReadOrUnread(isRead) {
            const userStore = useUserStore();
            const currentUserId = userStore.authUserId;

            if (this.seenBySet.has(currentUserId) == isRead) {
                // Redundant call
                return
            }

            this.loading = true;
            await api.interactions.markInteractionsAsReadOrUnread(
                [this.interaction_id],
                isRead,
            );


            if (isRead) {
                if (!this.seenBySet.has(currentUserId)) {
                    this.interaction.seen_by.push(currentUserId);
                }
            } else {
                this.interaction.seen_by = this.interaction.seen_by.filter(
                    (userId) => userId !== currentUserId,
                );
            }
            this.loading = false;
        },
        async addTag(tag) {
            if (this.tagSet.has(tag)) {
                return;
            }

            this.loading = true;
            await api.interaction_tags.createInteractionTag({
                tag: tag,
                interaction_id: this.interaction._id,
            });
            this.interaction.user_tags.push(tag);
            this.loading = false;
        },
        async removeTag(tag) {
            if (!this.tagSet.has(tag)) {
                return;
            }

            this.loading = true;
            await api.interaction_tags.deleteInteractionTag({
                tag: tag,
                interaction_id: this.interaction._id,
            });
            this.interaction.user_tags = this.interaction.user_tags.filter(
                (t) => t !== tag,
            );
            this.loading = false;
        },
        async updateStatus(newStatus, sendNotifications) {
            if (this.interaction.status === newStatus) {
                return;
            }

            this.loading = true;
            this.interaction = await api.interactions.updateInteraction(
                this.interaction._id,
                {
                  status: newStatus,
                  send_notifications: sendNotifications === false ? false : true,
                },
            )

            if (newStatus === InteractionStatus2Enum.TICKET_CLOSED) {
                this.isEditorOpen = false;
            }
            this.loading = false;
        },
        async updateDepartment(newDeptId) {
            if (this.interaction.department === newDeptId) {
                return;
            }

            this.loading = true;
            this.interaction = await api.interactions.updateInteraction(
                this.interaction._id,
                { department: newDeptId },
            )
            this.loading = false
        },
        async updateAssignees(assigneesPayload) {
            this.loading = true;
            this.interaction = await api.interactions.updateInteraction(this.interaction._id, assigneesPayload);
            this.loading = false;
        },
        async closeTicket(openTraining, sendNotifications) {
            this.loading = true;
            this.updateStatus(InteractionStatus2Enum.TICKET_CLOSED, sendNotifications);
            if (openTraining) {
                this.isTrainingPopupOpen = true;
            }
            this.isCloseTicketModalOpen = false;
            this.loading = false;
        },
        async convertToTicket() {
            this.loading = true;
            this.updateStatus(InteractionStatus2Enum.TICKET);
            this.isConvertToTicketModalOpen = false;
            this.activeTab = TAB_VALUE.CONVERSATIONS;
            this.isEditorOpen = true;
            this.loading = false;
        },
        async reopenTicket() {
            this.loading = true;
            this.updateStatus(InteractionStatus2Enum.TICKET);
            this.isReopenTicketModalOpen = false;
            this.activeTab = TAB_VALUE.CONVERSATIONS;
            this.isEditorOpen = true;
            this.loading = false;
        },
        async updateTraining() {
            if (this.hasExistingTraining) {
                this.training = await api.trainings.updateTraining(this.training._id, this.training);
            } else {
                this.training = await api.trainings.createTraining(this.training);
            }
        },
        async resetTraining() {
            this.training = _getDefaultTraining(this.interaction)
        },
        closeEditor() {
            this.draftResponse = {};
            this.editorValues = {};
            this.isEditorOpen = false;
        },
        openEditor() {
            this.isEditorOpen = true;
        },
        setInteractionIdFromDraftResponse() {
            const id = this.draftResponse?.interaction_id ?? this.draftResponse?.id;
            if (id && !this.interaction?._id && !this.interaction?.id) {
                this.interaction = { ...this.interaction, _id: id, id };
            }
        },
        addOrUpdateDraftInList(draft) {
            const list = this.ticketResponseDrafts ?? [];
            const draftId = draft?._id ?? draft?.id;
            const idx = draftId ? list.findIndex((d) => (d._id ?? d.id) === draftId) : -1;
            if (idx >= 0) {
                this.ticketResponseDrafts = list.map((d, i) => (i === idx ? draft : d));
            } else {
                this.ticketResponseDrafts = [draft, ...list];
            }
        },
    },
});

const _getDefaultTraining = (interaction) => {
    const userStore = useUserStore();
    return {
        updated_query: interaction.query,
        Data: interaction.response,
        Status: TrainingStatusEnum.REVIEW,
        user_id_pv2: userStore.authUserId,
        department: interaction.department,
        Interaction: interaction._id,
        is_new: true
    }
}
